import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/modules/common/api/index.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { NOTIFICATIONS_LIST_QUERY_KEY } from '@/modules/issuer-dashboard/queries/event-logs.ts';
import {
  GetHolderTaxReportByYearDataResponse,
  GetRegisterTotalFundByYearListResponse,
  GetRegisterTotalFundByYearRegisterHoldersResponse,
  GetRegisterTotalFundByYearRegisterHoldersV1SrTaxReportRegisterTotalFundByYearHoldersGetParams,
  RegisterTotalFundByYearListItem,
} from 'api';

export const REGISTER_TOTAL_FUND_BY_YEAR_QUERY_KEY = 'vestra/register-total-fund-by-year';
export const REGISTER_TOTAL_FUND_BY_YEAR_BY_REGISTER_QUERY_KEY =
  'vestra/register-total-fund-by-year-by-register';
export const REGISTER_TOTAL_FUND_BY_YEAR_REGISTER_HOLDERS_QUERY_KEY =
  'vestra/register-total-fund-by-year-register-holders';
export const HOLDER_TAX_REPORT_BY_YEAR_DATA_QUERY_KEY = 'vestra/holder-tax-report-by-year-data';
export const HOLDER_TAX_REPORT_YEARS_QUERY_KEY = 'vestra/holder-tax-report-years';

export interface UpsertRegisterTotalFundByYearRequest {
  register_id: number;
  year: number;
  total_fund: number;
}

export interface UpsertHolderTaxReportByYearDataRequest {
  year: number;
  entity_id: number;
  register_id: number;
  tax_report_json: Record<string, unknown>;
}

export interface HolderTaxReportYearListItem {
  year: number;
  id: number;
  entity_id: number;
  register_id: number;
  register_name: string;
  created_at: string;
}

export interface HolderTaxReportYearsResponse {
  data: HolderTaxReportYearListItem[];
}

export const useRegisterTotalFundByYearListQuery = (year: number) => {
  return useQuery<GetRegisterTotalFundByYearListResponse>({
    queryKey: [REGISTER_TOTAL_FUND_BY_YEAR_QUERY_KEY, year],
    queryFn: async () => {
      const response = await api.instance.get<GetRegisterTotalFundByYearListResponse>(
        '/v1/sr/tax-report/register-total-fund-by-year',
        { params: { year } },
      );

      return response.data;
    },
    enabled: Number.isFinite(year),
  });
};

export const useRegisterTotalFundByYearRegisterHoldersQuery = (
  payload: GetRegisterTotalFundByYearRegisterHoldersV1SrTaxReportRegisterTotalFundByYearHoldersGetParams,
  disabled = false,
) => {
  return useQuery<GetRegisterTotalFundByYearRegisterHoldersResponse>({
    queryKey: [REGISTER_TOTAL_FUND_BY_YEAR_REGISTER_HOLDERS_QUERY_KEY, JSON.stringify(payload)],
    queryFn: async () => {
      const response =
        await api.v1.getRegisterTotalFundByYearRegisterHoldersV1SrTaxReportRegisterTotalFundByYearHoldersGet(
          payload,
        );
      return response.data;
    },
    enabled:
      !disabled &&
      Number.isFinite(payload.register_id) &&
      payload.register_id > 0 &&
      Number.isFinite(payload.year) &&
      payload.year > 0,
  });
};

export const useRegisterTotalFundByYearQuery = (payload: { year: number; register_id: number }) => {
  return useQuery<RegisterTotalFundByYearListItem | undefined>({
    queryKey: [REGISTER_TOTAL_FUND_BY_YEAR_BY_REGISTER_QUERY_KEY, JSON.stringify(payload)],
    queryFn: async () => {
      const response = await api.instance.get<GetRegisterTotalFundByYearListResponse>(
        '/v1/sr/tax-report/register-total-fund-by-year',
        { params: { year: payload.year } },
      );

      return response.data.registers.find(item => item.register_id === payload.register_id);
    },
    enabled:
      Number.isFinite(payload.year) && payload.year > 0 && Number.isFinite(payload.register_id) && payload.register_id > 0,
  });
};

export const useUpsertRegisterTotalFundByYearMutation = () => {
  return useMutation({
    mutationFn: async (payload: UpsertRegisterTotalFundByYearRequest) => {
      const response = await withErrorHandling(
        api.instance.put('/v1/sr/tax-report/register-total-fund-by-year', payload),
        {
          loading: 'Saving total fund...',
          success: 'Total fund saved',
        },
      );

      return response.data;
    },
    onSuccess: async (_data, payload) => {
      await queryClient.invalidateQueries({
        queryKey: [REGISTER_TOTAL_FUND_BY_YEAR_QUERY_KEY, payload.year],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          REGISTER_TOTAL_FUND_BY_YEAR_BY_REGISTER_QUERY_KEY,
          JSON.stringify({ year: payload.year, register_id: payload.register_id }),
        ],
      });
      await queryClient.invalidateQueries({
        queryKey: [NOTIFICATIONS_LIST_QUERY_KEY],
      });
    },
  });
};

export const useHolderTaxReportByYearDataQuery = (
  payload: { year: number; entity_id: number; register_id: number },
  disabled = false,
) => {
  return useQuery<GetHolderTaxReportByYearDataResponse>({
    queryKey: [HOLDER_TAX_REPORT_BY_YEAR_DATA_QUERY_KEY, JSON.stringify(payload)],
    queryFn: async () => {
      const response = await api.instance.get<GetHolderTaxReportByYearDataResponse>(
        '/v1/sr/tax-report/holder-by-year-data',
        {
          params: payload,
          headers: {
            ['xRegister-ID']: String(payload.register_id),
          },
        },
      );
      return response.data;
    },
    enabled:
      !disabled &&
      Number.isFinite(payload.year) &&
      payload.entity_id > 0 &&
      payload.register_id > 0,
  });
};

export const useUpsertHolderTaxReportByYearDataMutation = () => {
  return useMutation({
    mutationFn: async (payload: UpsertHolderTaxReportByYearDataRequest) => {
      const response = await withErrorHandling(
        api.instance.put('/v1/sr/tax-report/holder-by-year-data', payload, {
          headers: {
            ['xRegister-ID']: String(payload.register_id),
          },
        }),
        {
          loading: 'Saving tax report...',
          success: 'Tax report saved',
        },
      );

      return response.data;
    },
    onSuccess: async (_data, payload) => {
      await queryClient.invalidateQueries({
        queryKey: [
          HOLDER_TAX_REPORT_BY_YEAR_DATA_QUERY_KEY,
          JSON.stringify({
            year: payload.year,
            entity_id: payload.entity_id,
            register_id: payload.register_id,
          }),
        ],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          HOLDER_TAX_REPORT_YEARS_QUERY_KEY,
          JSON.stringify({
            entity_id: payload.entity_id,
            register_id: payload.register_id,
          }),
        ],
      });
      await queryClient.invalidateQueries({
        queryKey: [NOTIFICATIONS_LIST_QUERY_KEY],
      });
    },
  });
};

export const useHolderTaxReportYearsQuery = (
  payload: { entity_id: number; register_id: number },
  disabled = false,
) => {
  return useQuery<HolderTaxReportYearsResponse>({
    queryKey: [HOLDER_TAX_REPORT_YEARS_QUERY_KEY, JSON.stringify(payload)],
    queryFn: async () => {
      const response = await api.instance.get<HolderTaxReportYearsResponse>(
        '/v1/sr/tax-report/holder-by-year-data/years',
        {
          params: payload,
          headers: {
            ['xRegister-ID']: String(payload.register_id),
          },
        },
      );
      return response.data;
    },
    enabled: !disabled && payload.entity_id > 0 && payload.register_id > 0,
  });
};
